import { Supabase } from "../Utils/supabase.config";

// Create a new comment or reply
export const createComment = async (commentData) => {
    try {
        const { data, error } = await Supabase
            .from('comments')
            .insert({
                userId: commentData.userId,
                postId: commentData.postId,
                text: commentData.text // Use 'text' field
            })
            .select(`
                *,
                user: users ( id, name, image )
            `)
            .single();

        if (error) {
            console.error('Comment creation error:', error);
            return { success: false, msg: error.message || "Could not create your comment" };
        }
        return { success: true, data: data };
    } catch (error) {
        console.error('Comment creation exception:', error);
        return { success: false, msg: "Could not create your comment" };
    }
};

// Create a reply to a comment
export const createReply = async (replyData) => {
    try {
        const { data, error } = await Supabase
            .from('comments')
            .insert({
                userId: replyData.userId,
                postId: replyData.postId,
                text: replyData.text, // Use 'text' field
                parent_id: replyData.parentId // Map parentId to parent_id
            })
            .select(`
                *,
                user: users ( id, name, image )
            `)
            .single();

        if (error) {
            console.error('Reply creation error:', error);
            return { success: false, msg: error.message || "Could not create your reply" };
        }
        return { success: true, data: data };
    } catch (error) {
        console.error('Reply creation exception:', error);
        return { success: false, msg: "Could not create your reply" };
    }
};

// Fetch comments for a post with nested structure
export const fetchPostComments = async (postId) => {
    try {
        // First get all comments for the post
        const { data: allComments, error: allError } = await Supabase
            .from('comments')
            .select(`
                *,
                user: users ( id, name, image )
            `)
            .eq('postId', postId)
            .order('created_at', { ascending: true });

        if (allError) {
            console.error('Comments fetch error:', allError);
            return { success: false, msg: allError.message || "Could not fetch comments" };
        }

        // Separate top-level comments and replies
        const topLevelComments = (allComments || []).filter(comment => !comment.parent_id);
        const replies = (allComments || []).filter(comment => comment.parent_id);

        // Group replies under their parent comments
        const commentsWithReplies = topLevelComments.map(comment => ({
            ...comment,
            replies: replies.filter(reply => reply.parent_id === comment.id)
        }));

        return { success: true, data: commentsWithReplies };
    } catch (error) {
        console.error('Comments fetch exception:', error);
        return { success: false, msg: "Could not fetch comments" };
    }
};

// Fetch replies for a specific comment
export const fetchCommentReplies = async (commentId) => {
    try {
        const { data, error } = await Supabase
            .from('comments')
            .select(`
                *,
                user: users ( id, name, image )
            `)
            .eq('parent_id', commentId)
            .order('created_at', { ascending: true });

        if (error) {
            return { success: false, msg: "Could not fetch replies" };
        }
        return { success: true, data: data || [] };
    } catch (error) {
        return { success: false, msg: "Could not fetch replies" };
    }
};

// Delete a comment (and all its replies)
export const deleteComment = async (commentId) => {
    try {
        const { error } = await Supabase
            .from('comments')
            .delete()
            .eq('id', commentId);

        if (error) {
            return { success: false, msg: "Could not delete the comment" };
        }
        return { success: true, data: { commentId } };
    } catch (error) {
        return { success: false, msg: "Could not delete the comment" };
    }
};

// Update a comment
export const updateComment = async (commentId, newBody) => {
    try {
        const { data, error } = await Supabase
            .from('comments')
            .update({ body: newBody })
            .eq('id', commentId)
            .select(`
                *,
                user: users ( id, name, image )
            `)
            .single();

        if (error) {
            return { success: false, msg: "Could not update the comment" };
        }
        return { success: true, data: data };
    } catch (error) {
        return { success: false, msg: "Could not update the comment" };
    }
};

// Get comment count for a post (including replies)
export const getCommentCount = async (postId) => {
    try {
        const { count, error } = await Supabase
            .from('comments')
            .select('*', { count: 'exact', head: true })
            .eq('postId', postId);

        if (error) {
            return { success: false, msg: "Could not get comment count" };
        }
        return { success: true, data: count || 0 };
    } catch (error) {
        return { success: false, msg: "Could not get comment count" };
    }
};

// Subscribe to comment changes for real-time updates
export const subscribeToComments = (postId, onCommentChange) => {
    const channel = Supabase
        .channel(`comments-${postId}`)
        .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'comments',
            filter: `postId=eq.${postId}`
        }, onCommentChange)
        .subscribe();

    return channel;
};

// Subscribe to reply changes for a specific comment
export const subscribeToReplies = (commentId, onReplyChange) => {
    const channel = Supabase
        .channel(`replies-${commentId}`)
        .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'comments',
            filter: `parent_id=eq.${commentId}`
        }, onReplyChange)
        .subscribe();

    return channel;
};
